// File: src/components/Modals/MaterialModal.jsx
import React, { useEffect, useMemo, useRef, useState } from "react";
import { createPortal } from "react-dom";
import toast from "react-hot-toast";
import { compressImage } from "../../utils/imageCompression";
import { useMaterials } from "../../contexts/MaterialContext";
import { useStorage } from "../../contexts/StorageContext";
import CameraCapture from "../Common/CameraCapture";

/**
 * Props:
 * - initialMaterial
 * - onClose
 * - onSave(material)
 * - forceCategory?: string   // e.g. "Packaging" (locks the category picker)
 */

const defaultCategories = ["Clay", "Glaze", "Sticker", "Label", "Tool"];
const trackingUnits = ["gram", "kg", "ml", "litre", "piece", "sheet", "custom", "unit"];
const purchaseUnits = ["bag", "box", "bottle", "jar", "roll", "sheet", "pouch", "pack", "custom", "unit"];

const inputGlass =
  "w-full px-3 py-2 rounded bg-black/30 border border-white/20 text-white placeholder:text-white/60 outline-none";
const pill =
  "px-4 py-2 rounded text-sm bg-zinc-700 hover:bg-zinc-600 text-white";
const primary =
  "px-4 py-2 rounded text-sm bg-orange-500 hover:bg-orange-600 text-white";
const secondary = "px-4 py-2 rounded text-sm bg-gray-600 text-white";
const tabBtn = (active) =>
  `px-4 py-2 rounded ${active ? "bg-orange-500 text-white" : "bg-zinc-700 text-white hover:bg-zinc-600"}`;

const MAX_IMAGE_BYTES = 2 * 1024 * 1024;

const SUPPLIERS = [
  { name: "Keane Ceramics", href: "https://keaneceramics.com.au/" },
  { name: "Clay Sydney", href: "https://claysydney.com/" },
];

function toNum(v, d = 0) {
  const n = Number(v);
  return Number.isFinite(n) ? n : d;
}
function uniqueSku(base, existingSkus) {
  const used = new Set((existingSkus || []).filter(Boolean));
  let candidate = base;
  let i = 1;
  while (used.has(candidate)) {
    candidate = `${base}-${i}`;
    i = i + 1;
  }
  return candidate;
}
function detectLocation() {
  try {
    const raw = JSON.parse(localStorage.getItem("mudoUserSettings") || "{}");
    const parts = [raw.location, raw.city, raw.region, raw.state, raw.country, raw.countryName]
      .filter(Boolean)
      .map((s) => String(s).trim());
    const fromSettings = parts.find((p) => p.length > 1);
    if (fromSettings) return fromSettings;
  } catch {}
  try {
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone || "";
    const city = tz.split("/")[1]?.replace(/_/g, " ");
    if (city) return city;
  } catch {}
  try {
    const lang = navigator.language || "";
    const m = lang.match(/-([A-Z]{2})$/);
    const cc = m?.[1];
    if (cc) {
      const map = { AU: "Australia", NZ: "New Zealand", US: "United States", CA: "Canada", GB: "United Kingdom", IE: "Ireland" };
      return map[cc] || cc;
    }
  } catch {}
  return "";
}
const buildGoogleUrl = (q) => `https://www.google.com/search?q=${encodeURIComponent(q || "")}`;

export default function MaterialModal({ initialMaterial, onClose, onSave, forceCategory }) {
  const { materials } = useMaterials();
  const { loadJSON, saveJSON } = useStorage();

  // Treat as edit mode ONLY if an id exists; blank objects are "add" mode
  const editMode = !!(initialMaterial && initialMaterial.id != null);

  const [tab, setTab] = useState("basic");
  const [cats, setCats] = useState(defaultCategories);
  const [errors, setErrors] = useState({});
  const [imgError, setImgError] = useState("");

  const dropRef = useRef(null);
  const fileInputRef = useRef(null);

  // NEW: real camera overlay
  const [cameraOpen, setCameraOpen] = useState(false);

  // Load categories from app storage (persisted + synced), fallback to defaults
  useEffect(() => {
    let alive = true;
    (async () => {
      try {
        const stored = await loadJSON("materials.categories.json", defaultCategories);
        if (alive && Array.isArray(stored) && stored.length) setCats(stored);
      } catch {
        // ignore; keep defaults
      }
    })();
    return () => { alive = false; };
  }, [loadJSON]);

  const [form, setForm] = useState(
    editMode
      ? {
          id: initialMaterial?.id ?? null,
          name: initialMaterial?.name || "",
          sku: initialMaterial?.sku || "",
          category: initialMaterial?.category || "",
          supplierName: initialMaterial?.supplierName || "",
          supplierLink: initialMaterial?.supplierLink || "",
          image: initialMaterial?.image || null,
          inventoriable: typeof initialMaterial?.inventoriable === "boolean" ? initialMaterial.inventoriable : true,
          trackingUnit: initialMaterial?.trackingUnit || "gram",
          trackingUsage: initialMaterial?.trackingUsage || "",
          purchaseUnit: initialMaterial?.purchaseUnit || "bag",
          conversionAmount: initialMaterial?.conversionAmount || "",
          lowStockLimit: initialMaterial?.lowStockLimit || "",
          startDate: initialMaterial?.startDate || "",
          startQuantity: initialMaterial?.startQuantity || "",
          purchaseCost: initialMaterial?.purchaseCost || "",
          note: initialMaterial?.note || "",
        }
      : {
          id: null,
          name: "",
          sku: "",
          category: forceCategory || "",
          supplierName: "",
          supplierLink: "",
          image: null,
          inventoriable: true,
          trackingUnit: "gram",
          trackingUsage: "",
          purchaseUnit: "bag",
          conversionAmount: "",
          lowStockLimit: "",
          startDate: "",
          startQuantity: "",
          purchaseCost: "",
          note: "",
        }
  );

  // keep category locked if forced
  useEffect(() => {
    if (forceCategory && form.category !== forceCategory) {
      setForm((f) => ({ ...f, category: forceCategory }));
    }
  }, [forceCategory]); // eslint-disable-line

  // Auto SKU (add mode only)
  useEffect(() => {
    if (editMode) return;
    const cat = form.category || forceCategory || "MAT";
    const prefix = (cat || "MAT").slice(0, 3).toUpperCase();
    const frag = (form.name || "NEW").slice(0, 3).toUpperCase();
    const ts = Date.now().toString().slice(-4);
    const base = `${prefix}-${frag}-${ts}`;
    const existingSkus = (materials || []).map((m) => m.sku);
    setForm((f) => (f.sku ? f : { ...f, sku: uniqueSku(base, existingSkus) }));
  }, [form.name, form.category, forceCategory, editMode, materials]);

  const change = (e) => {
    const { name, value, type, checked, files } = e.target;
    setForm((f) => ({
      ...f,
      [name]: type === "checkbox" ? checked : type === "file" ? files?.[0] || null : value,
    }));
  };
  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  // image handling
  async function acceptImageFile(file) {
    if (!file) return;
    setImgError("");
    const allowed = ["image/png", "image/jpeg", "image/webp", "image/svg+xml", "image/heic", "image/heif"];
    if (!allowed.includes(file.type)) {
      setImgError("Please upload PNG, JPG/JPEG, WEBP, or SVG.");
      return;
    }
    try {
      const { dataURL } = await compressImage(file, {
        maxBytes: MAX_IMAGE_BYTES,
        maxWidth: 2000,
        maxHeight: 2000,
        preferredMime: "image/webp",
      });
      setField("image", dataURL);
    } catch (e) {
      console.error("Image compression failed:", e);
      setImgError("Could not process that image.");
    }
  }
  const onFileChange = async (e) => {
    const f = e.target.files?.[0];
    if (f) await acceptImageFile(f);
    e.target.value = "";
  };
  const pickFile = () => fileInputRef.current?.click();

  // Camera: open overlay and accept Blob → compress → set image
  const openCamera = () => setCameraOpen(true);
  const closeCamera = () => setCameraOpen(false);
  const onCameraCapture = async (blob) => {
    try {
      // Convert Blob to File so compressImage can handle it uniformly
      const file = new File([blob], "capture.jpg", { type: "image/jpeg" });
      await acceptImageFile(file);
      toast.success("Photo captured");
    } catch (e) {
      toast.error("Couldn't use that photo");
    } finally {
      closeCamera();
    }
  };

  const removeImage = () => setField("image", null);

  // dnd and paste
  useEffect(() => {
    const el = dropRef.current;
    if (!el) return;
    const stop = (e) => { e.preventDefault(); e.stopPropagation(); };
    const onDrop = async (e) => {
      stop(e);
      const f = e.dataTransfer?.files?.[0];
      if (f) await acceptImageFile(f);
      el.classList.remove("ring-2", "ring-amber-400");
    };
    const onDragOver = (e) => { stop(e); el.classList.add("ring-2", "ring-amber-400"); };
    const onDragLeave = (e) => { stop(e); el.classList.remove("ring-2", "ring-amber-400"); };
    const onPaste = async (e) => {
      const items = e.clipboardData?.items || [];
      for (const it of items) {
        if (it.type?.startsWith("image/")) {
          const f = it.getAsFile();
          if (f) { await acceptImageFile(f); break; }
        }
      }
    };
    el.addEventListener("dragover", onDragOver);
    el.addEventListener("dragleave", onDragLeave);
    el.addEventListener("drop", onDrop);
    el.addEventListener("dragenter", stop);
    el.addEventListener("dragend", stop);
    el.addEventListener("paste", onPaste);
    return () => {
      el.removeEventListener("dragover", onDragOver);
      el.removeEventListener("dragleave", onDragLeave);
      el.removeEventListener("drop", onDrop);
      el.removeEventListener("dragenter", stop);
      el.removeEventListener("dragend", stop);
      el.removeEventListener("paste", onPaste);
    };
  }, []);

  const unitPrice = useMemo(() => {
    const conv = toNum(form.conversionAmount, 0);
    const cost = toNum(form.purchaseCost, 0);
    return conv > 0 ? cost / conv : 0;
  }, [form.conversionAmount, form.purchaseCost]);

  const validate = () => {
    const e = {};
    if (!form.name?.trim()) e.name = "Name is required";
    if (!form.sku?.trim()) e.sku = "SKU failed to generate";
    if (!form.trackingUnit?.trim()) e.trackingUnit = "Unit is required";

    // Relaxed rule for Packaging:
    // - If purchaseCost is given, require conversionAmount > 0.
    // - If purchaseCost is empty, allow conversionAmount to be empty.
    const cost = toNum(form.purchaseCost, 0);
    const conv = toNum(form.conversionAmount, 0);
    if (cost > 0 && !(conv > 0)) e.conversionAmount = "Must be greater than 0 when a cost is set";

    if (toNum(form.startQuantity, 0) < 0) e.startQuantity = "Cannot be negative";
    if (toNum(form.lowStockLimit, 0) < 0) e.lowStockLimit = "Cannot be negative";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  // Normalize for Packaging on save: set defaults if blank
  const normalizeForPackaging = (draft) => {
    const d = { ...draft };
    if (forceCategory) d.category = forceCategory;
    if (!d.trackingUnit) d.trackingUnit = "unit";
    if (!d.purchaseUnit) d.purchaseUnit = "unit";
    if (!d.conversionAmount) d.conversionAmount = "1";
    return d;
  };

  const doSave = (after) => {
    // If category is forced (Packaging), ensure permissive defaults
    const draft = forceCategory ? normalizeForPackaging(form) : form;

    if (!validate()) {
      toast.error("Please fix the highlighted fields.");
      return;
    }
    try {
      const payload = {
        ...draft,
        id: draft.id ?? initialMaterial?.id ?? null,
        category: forceCategory || draft.category || "",
        unitPrice:
          draft.conversionAmount && draft.purchaseCost
            ? Number(draft.purchaseCost) / Number(draft.conversionAmount)
            : unitPrice,
      };
      onSave?.(payload);
      toast.success(editMode ? "Updated" : "Saved");
      after?.();
    } catch (err) {
      console.error(err);
      toast.error("Could not save material.");
    }
  };
  const save = () => doSave(onClose);
  const saveAdd = () =>
    doSave(() =>
      setForm({
        id: null,
        name: "",
        sku: "",
        category: forceCategory || "",
        supplierName: "",
        supplierLink: "",
        image: null,
        inventoriable: true,
        trackingUnit: "gram",
        trackingUsage: "",
        purchaseUnit: "bag",
        conversionAmount: "",
        lowStockLimit: "",
        startDate: "",
        startQuantity: "",
        purchaseCost: "",
        note: "",
      })
    );

  // Categories: add/remove now persist to Storage
  const addCategory = async () => {
    if (forceCategory) return; // locked
    const n = prompt("New category");
    if (!n) return;
    const next = Array.from(new Set([...(cats || []), n.trim()]));
    setCats(next);
    try { await saveJSON("materials.categories.json", next); } catch {}
    setForm((f) => ({ ...f, category: n.trim() }));
  };
  const removeCategory = async () => {
    if (forceCategory || !form.category) return;
    if (!confirm(`Remove “${form.category}” from list?`)) return;
    const next = (cats || []).filter((c) => c !== form.category);
    setCats(next);
    try { await saveJSON("materials.categories.json", next); } catch {}
    setForm((f) => ({ ...f, category: "" }));
  };

  const locationHint = useMemo(() => detectLocation(), []);
  const queryBits = ["ceramics materials", locationHint, form.name, form.sku, form.supplierName].filter(Boolean).join(" ");
  const portalTarget = document.getElementById("modal-root") || document.body;

  const isCategoryLocked = !!forceCategory;

  return createPortal(
    <div className="fixed inset-0 z-[10000] flex items-center justify-center bg-black/60 backdrop-blur-sm">
      <div className="relative w-full max-w-6xl mx-4">
        <div className="bg-white/10 dark:bg-black/40 text-white rounded-xl border border-white/20 shadow-2xl overflow-hidden">
          {/* Header */}
          <div className="px-6 py-4 border-b border-white/15 flex items-center justify-between">
            <div>
              <h3 className="text-lg font-semibold">{editMode ? "Edit Material" : "Add Material"}</h3>
              <p className="text-xs opacity-70">Set costs in the package’s unit — we’ll compute per-unit price.</p>
            </div>
            <button onClick={onClose} className={pill}>Close</button>
          </div>

          {/* Tabs */}
          <div className="px-6 pt-4 flex gap-3">
            <button className={tabBtn(tab === "basic")} onClick={() => setTab("basic")}>Basic Info</button>
            <button className={tabBtn(tab === "stock")} onClick={() => setTab("stock")}>Stock & Cost</button>
          </div>

          {/* Body */}
          <div className="p-6 grid grid-cols-1 md:grid-cols-4 gap-6">
            {/* Left: Image */}
            <div className="md:col-span-1">
              <label className="block text-sm mb-2 opacity-80">Image</label>
              <div
                ref={dropRef}
                className="aspect-square rounded-lg border border-white/20 bg-black/30 flex items-center justify-center overflow-hidden"
                onClick={() => fileInputRef.current?.click()}
              >
                {form.image ? (
                  <img src={typeof form.image === "string" ? form.image : ""} alt={form.name || "Material"} className="h-full w-full object-cover" />
                ) : (
                  <div className="text-xs opacity-70 text-center px-3">Drag & drop a photo here<br />or click / paste</div>
                )}
              </div>
              {imgError && <p className="text-xs text-red-300 mt-2">{imgError}</p>}
              <div className="flex gap-2 mt-3">
                <input ref={fileInputRef} type="file" accept="image/*" className="hidden" onChange={onFileChange} />
                <button onClick={pickFile} className="px-3 py-1 rounded bg-amber-400 text-black text-sm hover:bg-amber-300">Upload</button>
                <button onClick={openCamera} className="px-3 py-1 rounded bg-emerald-400 text-black text-sm hover:bg-emerald-300">Camera</button>
                {form.image && <button onClick={removeImage} className="ml-auto px-3 py-1 rounded bg-red-600 text-white text-sm hover:bg-red-500">Remove</button>}
              </div>
              <p className="mt-2 text-[11px] text-white/60">Images are automatically resized under 2 MB.</p>
            </div>

            {/* Middle: Fields */}
            {tab === "basic" ? (
              <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div className="sm:col-span-2">
                  <label className="block text-sm mb-1">Name *</label>
                  <input name="name" value={form.name} onChange={change} className={inputGlass} placeholder="e.g., Keane White Raku" />
                  {errors.name && <p className="text-xs text-red-300 mt-1">{errors.name}</p>}
                </div>
                <div>
                  <label className="block text-sm mb-1">Category</label>
                  <div className="flex items-center gap-2">
                    <select
                      name="category"
                      value={forceCategory || form.category}
                      onChange={change}
                      disabled={isCategoryLocked}
                      className={`${inputGlass} grow ${isCategoryLocked ? "opacity-70 cursor-not-allowed" : ""}`}
                    >
                      <option value=""></option>
                      {cats.map((c) => <option key={c} value={c}>{c}</option>)}
                    </select>
                    {isCategoryLocked && (
                      <span className="text-[11px] px-2 py-1 rounded bg-white/15 border border-white/20">
                        Locked: {forceCategory}
                      </span>
                    )}
                    {!isCategoryLocked && (
                      <>
                        <button type="button" onClick={addCategory} className="px-3 rounded bg-yellow-400 text-black text-sm hover:bg-yellow-300">Add</button>
                        <button type="button" onClick={removeCategory} disabled={!form.category} className="px-3 rounded bg-zinc-700 text-white text-sm disabled:opacity-40">Remove</button>
                      </>
                    )}
                  </div>
                </div>
                <div>
                  <label className="block text-sm mb-1">SKU</label>
                  <input name="sku" value={form.sku} readOnly className={`${inputGlass} cursor-not-allowed`} />
                  {errors.sku && <p className="text-xs text-red-300 mt-1">{errors.sku}</p>}
                </div>
                <div>
                  <label className="block text-sm mb-1">Supplier</label>
                  <input name="supplierName" value={form.supplierName} onChange={change} className={inputGlass} />
                </div>
                <div>
                  <label className="block text-sm mb-1">Website</label>
                  <input type="url" name="supplierLink" value={form.supplierLink} onChange={change} placeholder="https://" className={inputGlass} />
                </div>
                <div className="sm:col-span-2 flex items-center gap-2 mt-2">
                  <input id="inventoriable" type="checkbox" name="inventoriable" checked={!!form.inventoriable} onChange={change} />
                  <label htmlFor="inventoriable" className="text-sm">Track inventory & reorder alerts</label>
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm mb-1">Tracking Unit</label>
                  <div className="flex gap-2">
                    <select name="trackingUnit" value={form.trackingUnit} onChange={change} className={`${inputGlass} grow`}>
                      {trackingUnits.map((u) => <option key={u} value={u}>{u}</option>)}
                    </select>
                    <input name="trackingUsage" value={form.trackingUsage} onChange={change} placeholder="usage context (e.g., per mug)" className={inputGlass} />
                  </div>
                  {errors.trackingUnit && <p className="text-xs text-red-300 mt-1">{errors.trackingUnit}</p>}
                </div>
              </div>
            ) : (
              <div className="md:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm mb-1">Purchase Unit</label>
                  <select name="purchaseUnit" value={form.purchaseUnit} onChange={change} className={inputGlass}>
                    {purchaseUnits.map((u) => <option key={u} value={u}>{u}</option>)}
                  </select>
                </div>
                <div>
                  <label className="block text-sm mb-1">Conversion: 1 {form.purchaseUnit} =</label>
                  <input name="conversionAmount" value={form.conversionAmount} onChange={change} placeholder={`amount in ${form.trackingUnit}`} className={inputGlass} />
                  {errors.conversionAmount && <p className="text-xs text-red-300 mt-1">{errors.conversionAmount}</p>}
                </div>
                <div>
                  <label className="block text-sm mb-1">Low Stock Alert</label>
                  <input
                    name="lowStockLimit"
                    value={form.lowStockLimit}
                    onChange={change}
                    className={inputGlass}
                    placeholder="Alert me at this qty"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-1">Starting Qty</label>
                  <input
                    name="startQuantity"
                    value={form.startQuantity}
                    onChange={change}
                    className={inputGlass}
                    placeholder="Opening quantity"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-1">Start Date</label>
                  <input
                    type="date"
                    name="startDate"
                    value={form.startDate}
                    onChange={change}
                    className={`${inputGlass} min-w-0`}
                    placeholder="dd/mm/yyyy"
                  />
                </div>
                <div>
                  <label className="block text-sm mb-1">Purchase Cost ({form.purchaseUnit})</label>
                  <input
                    name="purchaseCost"
                    value={form.purchaseCost}
                    onChange={change}
                    className={`${inputGlass} min-w-0`}
                    placeholder={`Cost per ${form.purchaseUnit}`}
                  />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm mb-1">Unit Cost</label>
                  <input readOnly value={form.conversionAmount && form.purchaseCost ? `$${unitPrice.toFixed(4)} per ${form.trackingUnit}` : "—"} className={`${inputGlass} text-white/70`} />
                </div>
                <div className="sm:col-span-2">
                  <label className="block text-sm mb-1">Notes</label>
                  <textarea
                    name="note"
                    rows={3}
                    value={form.note}
                    onChange={change}
                    className={inputGlass}
                    placeholder="Optional: batch #, expiry, storage tips"
                  />
                </div>
              </div>
            )}

            {/* Right: Suppliers */}
            <aside className="md:col-span-1 space-y-3 bg-white/5 rounded-lg border border-white/10 p-4">
              <h4 className="font-semibold">Suppliers & Web</h4>
              <button type="button" className="w-full px-3 py-2 rounded bg-blue-600 text-white text-sm hover:bg-blue-700" onClick={() => window.open(buildGoogleUrl(queryBits), "_blank", "noopener,noreferrer")}>
                Search: “{queryBits || "ceramics materials"}”
              </button>
              {SUPPLIERS.map((s) => (
                <a key={s.name} href={s.href} target="_blank" rel="noopener noreferrer" className="block w-full px-3 py-2 text-center rounded bg-emerald-600 text-white text-sm hover:bg-emerald-700">
                  {s.name}
                </a>
              ))}
              <p className="text-xs text-white/70">Search uses your location ({detectLocation() || "unknown"}) plus Name, SKU and Supplier.</p>
              {form.supplierLink && (
                <a href={form.supplierLink} target="_blank" rel="noopener noreferrer" className="inline-block text-xs underline text-amber-300">
                  Visit supplier website ↗
                </a>
              )}
            </aside>
          </div>

          {/* Footer */}
          <div className="px-6 py-4 border-t border-white/15 flex items-center justify-end gap-2">
            <button onClick={onClose} className={secondary}>Cancel</button>
            {editMode ? (
              <button onClick={save} className={primary}>Save Changes</button>
            ) : (
              <>
                <button onClick={save} className={primary}>Save</button>
                <button onClick={saveAdd} className={pill}>Save & Add</button>
              </>
            )}
          </div>
        </div>
      </div>

      {/* Camera overlay (portal inside same root for stacking) */}
      {cameraOpen && (
        <div className="fixed inset-0 z-[10050] flex items-center justify-center">
          <div className="absolute inset-0 bg-black/60 backdrop-blur-sm" onClick={closeCamera} />
          <div className="relative w-full max-w-3xl mx-4 rounded-2xl overflow-hidden bg-white dark:bg-neutral-900 text-neutral-900 dark:text-white border border-white/10 shadow-2xl">
            <div className="flex items-center justify-between px-3 py-2 border-b border-black/10 dark:border-white/10">
              <div className="text-sm font-medium">Capture photo</div>
              <button className="px-2 py-1 text-xs rounded border border-black/10 dark:border-white/20" onClick={closeCamera}>
                Close
              </button>
            </div>
            <div className="p-3">
              <CameraCapture
                preferBackCamera
                onCapture={onCameraCapture}
                onClose={closeCamera}
              />
              <div className="text-xs opacity-70 px-1 pt-2">Tip: If the video is paused on iOS, tap “Start / Unblock”.</div>
            </div>
          </div>
        </div>
      )}
    </div>,
    portalTarget
  );
}
